import { useDialogFocus } from './useDialogFocus';
import { useMemo, useState } from 'react';
import type { Calendar, SubscriptionCreated } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { SubscriptionHelp } from './SubscriptionHelp';
import { SubscriptionPreview } from './SubscriptionPreview';

const MAX_FEED_CALENDARS = 20;

export function CalendarChoices({
  calendars,
  selected,
  onChange,
}: {
  calendars: Calendar[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <>
      {calendars.length > MAX_FEED_CALENDARS && (
        <p role="status">
          {selected.length} of {calendars.length} calendars selected. Choose up to{' '}
          {MAX_FEED_CALENDARS}.
          {selected.length >= MAX_FEED_CALENDARS && ' Uncheck a calendar to choose another.'}
        </p>
      )}
      <div className="calendar-choices">
        {calendars.map((calendar) => (
          <label key={calendar.id} className="calendar-choice">
            <input
              type="checkbox"
              checked={selected.includes(calendar.id)}
              disabled={!selected.includes(calendar.id) && selected.length >= MAX_FEED_CALENDARS}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...selected, calendar.id]
                    : selected.filter((id) => id !== calendar.id),
                )
              }
            />
            <span
              className="calendar-dot"
              style={{ backgroundColor: calendar.color }}
              aria-hidden="true"
            />
            <span>
              <b>{calendar.name}</b>
              <small>{calendar.description}</small>
            </span>
          </label>
        ))}
      </div>
    </>
  );
}

export function SubscriptionModal({
  calendars,
  session,
  onClose,
}: {
  calendars: Calendar[];
  session: ClientSession | null;
  onClose: () => void;
}) {
  const dialogRef = useDialogFocus(onClose);
  const publicCalendars = useMemo(
    () => calendars.filter((calendar) => calendar.visibility === 'public'),
    [calendars],
  );
  const [selected, setSelected] = useState(
    publicCalendars.slice(0, MAX_FEED_CALENDARS).map((calendar) => calendar.id),
  );
  const [created, setCreated] = useState<SubscriptionCreated | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    setError('');
    try {
      setCreated(await api.createSubscription(selected, session?.csrfToken));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the feed');
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (created) await navigator.clipboard.writeText(created.feedUrl);
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="modal subscribe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscribe-title"
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">Always up to date</span>
            <h2 id="subscribe-title">Personal calendar feed</h2>
          </div>
          <button className="icon-button" aria-label="Close subscription dialog" onClick={onClose}>
            ×
          </button>
        </header>
        {!created ? (
          <div className="modal-scroll">
            <p className="modal-lede">
              Choose the calendars you care about. Your private address keeps one combined calendar
              updated in Apple, Google, Outlook, or another calendar app.
            </p>
            <CalendarChoices calendars={calendars} selected={selected} onChange={setSelected} />
            <SubscriptionPreview calendarIds={selected} />
            {error && (
              <p className="inline-error" role="alert">
                {error}
              </p>
            )}
            <div className="privacy-note">
              <span aria-hidden="true">◌</span>
              <p>
                <b>Public calendars need no account or email</b>
                <br />A necessary browser cookie lets you manage this feed later.
              </p>
            </div>
          </div>
        ) : (
          <div className="modal-scroll subscription-result">
            <div className="success-mark" aria-hidden="true">
              ✓
            </div>
            <h3>Your calendar is ready</h3>
            <p>Keep this address private. Anyone with it can see the calendars in this feed.</p>
            <label className="field field-wide">
              <span>Subscription address</span>
              <div className="copy-field">
                <input readOnly value={created.feedUrl} />
                <button type="button" onClick={() => void copy()}>
                  Copy
                </button>
              </div>
            </label>
            <div className="calendar-apps">
              <a className="app-button" href={created.feedUrl.replace(/^https?:/, 'webcal:')}>
                <b>Apple Calendar</b>
                <small>Open subscription</small>
              </a>
              <a
                className="app-button"
                target="_blank"
                rel="noreferrer"
                href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(created.feedUrl)}`}
              >
                <b>Google Calendar</b>
                <small>Add by address</small>
              </a>
              <button className="app-button" type="button" onClick={() => void copy()}>
                <b>Outlook or other</b>
                <small>Copy the address</small>
              </button>
            </div>
            <a className="text-link management-link" href={created.managementUrl}>
              Manage this feed
            </a>
            <SubscriptionHelp />
            <details>
              <summary>Save a private recovery code</summary>
              <p>
                Save this code and the management link in your password manager. Together they let
                you manage this feed from another browser. Keep the code private and separate from
                the subscription address.
              </p>
              <label className="field">
                <span>Private recovery code</span>
                <input
                  readOnly
                  value={created.recoveryCredential}
                  onFocus={(event) => event.currentTarget.select()}
                />
              </label>
            </details>
          </div>
        )}
        <footer className="modal-actions">
          {created ? (
            <button className="button" onClick={onClose}>
              Done
            </button>
          ) : (
            <>
              <button className="button button-quiet" onClick={onClose}>
                Maybe later
              </button>
              <button
                className="button"
                disabled={!selected.length || busy}
                onClick={() => void create()}
              >
                {busy ? 'Creating…' : 'Create my feed'}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}
